import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { APPLICATION_STATUS_CODES, FORM_ACTIONS, FORM_IDENTIFIERS, OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS, POP_UP_CODES, QUESTION_CODES, SAVE_RESULT_STATUS } from 'src/app/constants/app-data.constants';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from 'src/app/constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from 'src/app/constants/templates.constants';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { AppDataService } from 'src/app/services/app-data.service';
import { CoreService, DataService, DialogService, FormConfigurationService, SaveDataRequest, SaveFormEvent, SpinnerService } from 'dspx-core';
import { ErrorService } from 'src/app/services/error.service';
import { ApplicationUserAssignRequestPopupQuestion } from 'src/app/interfaces/application-user-assign-request-popup-question.interface';
import { ApplicationUserAssignRequest } from 'src/app/models/application-user-assign-request';
import { finalize, take } from 'rxjs';
import { ConcessionsApplicationDetailsResponse } from 'src/app/interfaces/concessions-application-details-response.interface';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { ConcessionsApplicationFurtherReviewRequiredPopupQuestion } from 'src/app/interfaces/concessions-application-further-review-required-popup-question.interface';
import { ConcessionsApplicationOnHoldPopupQuestion } from 'src/app/interfaces/concessions-application-on-hold-popup-question.interface';
import { ConcessionsApplicationOnHoldRequest } from 'src/app/models/concessions-application-on-hold-request';
import { ConcessionsApplicationFurtherReviewRequiredRequest } from 'src/app/models/concessions-application-further-review-required-request';
import { ApplicationRejectPopupQuestion } from 'src/app/interfaces/application-reject-popup-question.interface';
import { ApplicationRejectRequest } from 'src/app/models/application-reject-request';
import { CentrelinkVerifiedAddressSaveRequest } from '../../interfaces/centrelink-verified-address-save-request';

@Component({
  selector: 'app-application-detail-concessions',
  templateUrl: './application-detail-concessions.component.html',
  styleUrl: './application-detail-concessions.component.scss'
})
export class ApplicationDetailConcessionsComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {
  fieldTypeReplacements = OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getConcessionsApplicationDetailsConfigAndResponse;

  templateId: number = TEMPLATES.concessionsApplicationDetails;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  response: ConcessionsApplicationDetailsResponse;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService
  ) {
    super(router, appDataService, dialogService);
    // Forces router to not to reuse the route. Otherwise navigation to same page with different querystring won't refresh the page.
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    }
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {
      home: () => this.home(),
      assign: () => this.assign(),
      accept: () => this.accept(),
      reject: () => this.reject(),
      onHold: () => this.onHold(),
      gotoCentrelinkCheck: () => this.gotoCentrelinkCheck(),
      addCentrelinkVerifiedAddress: () => this.addCentrelinkVerifiedAddress(),
      furtherReviewRequired: () => this.furtherReviewRequired(),
      gotoInvoices: () => this.gotoInvoices(),
      showAssignButton: () => this.showAssignButton(),
      showAcceptButton: () => this.showAcceptButton(),
      showRejectButton: () => this.showRejectButton(),
      showOnHoldButton: () => this.showOnHoldButton(),
      showCentrelinkCheckButton: () => this.showCentrelinkCheckButton(),
      showFurtherReviewRequiredButton: () => this.showFurtherReviewRequiredButton(),
      showCentrelinkVerifiedAddressPopupButton: () => this.showCentrelinkVerifiedAddressPopupButton(),
      showInvoicesButton: () => this.showInvoicesButton(),
      false: () => false,
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
        const formIdentifiers: any = [
          FORM_IDENTIFIERS.concessionsFormIdentifier,
          this.id, // application Id (or entityId)
        ];
        this.coreService.fileUploadFormIdentifiers[QUESTION_CODES.concessionsApplicationDetailsForm.applicationDocumentPlaceholder] = formIdentifiers;
      }
    });
  }

  assign() {
    const popupCode = POP_UP_CODES.concessionsApplicationDetails.assignApplicationUser
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode);
  }

  assignApplicationUser(popupQuestion: ApplicationUserAssignRequestPopupQuestion) {
    const request = new ApplicationUserAssignRequest(this.id, popupQuestion.assignedToUserIdPopup);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.concessionsApplicationDetails.assignApplicationUser, request)
        .pipe(take(1), this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner))
        .subscribe(response => {
          this.onAssignApplicationUserSuccess(response)
        }
        )
    );
  }

  private onAssignApplicationUserSuccess(response) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.reloadPage(true);
    }
  }

  showAssignButton() {
    return this.response.applicationStatusCode != APPLICATION_STATUS_CODES.draft
      && this.response.applicationStatusCode != APPLICATION_STATUS_CODES.rejected
      && this.response.applicationStatusCode != APPLICATION_STATUS_CODES.completed;
  }


  private accept() {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .postAndFetchData(`${API_ENDPOINTS.concessionsApplicationDetails.acceptApplication}/${this.id}`)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        }
        )
    );
  }

  showAcceptButton(): boolean {
    return this.isApplicationInProgressOrOnHoldAndAssignedToMe();
  }

  private reject() {
    this.showRejectApplicationDialog();
  }

  private showRejectApplicationDialog() {
    const popupCode = POP_UP_CODES.concessionsApplicationDetails.rejectApplication
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode);
  }

  private rejectApplication(popupQuestion: ApplicationRejectPopupQuestion) {
    const request = new ApplicationRejectRequest(this.id, popupQuestion.applicationRejectionReasonTypeId, null);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.concessionsApplicationDetails.rejectApplication, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(response => {
          if (response?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        }
        )
    );
  }

  showRejectButton(): boolean {
    return this.isApplicationInProgressOrOnHoldAndAssignedToMe();
  }

  onHold() {
    const popupCode = POP_UP_CODES.concessionsApplicationDetails.putApplicationOnHoldPopup
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode);
  }

  putApplicationOnHold(popupQuestion: ConcessionsApplicationOnHoldPopupQuestion) {
    const request = new ConcessionsApplicationOnHoldRequest(this.id, popupQuestion.applicationOnHoldReasonTypeId);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.concessionsApplicationDetails.putApplicationOnHold, request)
        .pipe(take(1), this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner))
        .subscribe(response => {
          this.onApplicationOnholdSuccess(response)
        }
        )
    );
  }

  private onApplicationOnholdSuccess(response) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.reloadPage(true);
    }
  }

  showOnHoldButton(): boolean {
    return !this.isApplicationInTerminalState();
  }

  gotoCentrelinkCheck() {
    const url = "https://business.centrelink.gov.au/";
    window.open(url, '_blank');
  }

  addCentrelinkVerifiedAddress() {
    const popupCode = POP_UP_CODES.concessionsApplicationDetails.addCentrelinkVerifiedAddressPopup;
    this.activeDialog = popupCode;

    const values = (this.response.centrelinkVerifiedAddress != null)
      ? {
          streetNumberName: this.response.centrelinkVerifiedAddress.streetNumberName,
          suburb: this.response.centrelinkVerifiedAddress.suburb,
          state: this.response.centrelinkVerifiedAddress.state,
          postCode: this.response.centrelinkVerifiedAddress.postCode
        }
      : null;

    this.dialogService.showDialog(popupCode, values);
  }

  showCentrelinkCheckButton(): boolean {
    return true;
  }

  furtherReviewRequired() {
    const popupCode = POP_UP_CODES.concessionsApplicationDetails.furtherReviewRequiredPopup;
    this.activeDialog = popupCode;
    this.dialogService.showDialog(popupCode);
  }

  applicationFurtherReviewRequired(popupQuestion: ConcessionsApplicationFurtherReviewRequiredPopupQuestion) {
    const request = new ConcessionsApplicationFurtherReviewRequiredRequest(this.id, popupQuestion.reviewReason);
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.concessionsApplicationDetails.furtherReviewRequired, request)
        .pipe(take(1), this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner))
        .subscribe(response => {
          this.onApplicationFurtherReviewRequiredSuccess(response)
        }
        )
    );
  }

  private onApplicationFurtherReviewRequiredSuccess(response) {
    this.spinnerService.hideSpinner();
    if (response?.status === SAVE_RESULT_STATUS.success) {
      this.reloadPage(true);
    }
  }

  showFurtherReviewRequiredButton() {
    return this.response.applicationStatusCode == APPLICATION_STATUS_CODES.accepted;
  }

  showCentrelinkVerifiedAddressPopupButton() {
    return (this.response.applicationStatusCode == APPLICATION_STATUS_CODES.onhold || this.response.applicationStatusCode == APPLICATION_STATUS_CODES.inprogress);
  }

  gotoInvoices() {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, this.id, ROUTE_CONSTANTS.concessionsApplicationDetail.invoiceListPrefix]);
  }

  showInvoicesButton() {
    return true;
  }

  isApplicationInProgressOrOnHoldAndAssignedToMe() {
    return (this.response.applicationStatusCode == APPLICATION_STATUS_CODES.inprogress || this.response.applicationStatusCode == APPLICATION_STATUS_CODES.onhold)
      && this.response.applicationAssignedTo == this.response.loggedInUserId
  }

  isApplicationInTerminalState(): boolean {
    return this.response.applicationStatusCode == APPLICATION_STATUS_CODES.rejected
      || this.response.applicationStatusCode == APPLICATION_STATUS_CODES.completed;
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded($event: any): void {
    this.response = $event as ConcessionsApplicationDetailsResponse;
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  actionButton_noteListPlaceholder_save_Clicked(event: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.note.save, { parentEntityId: this.id, noteContent: event.field.data,addUserDetails:true })
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();
          if (data?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
            // TODO: Handle errors.
            //if (SERVER_ERROR_POP_UP_CODES.saveReferenceData.includes(data.message)) {
            //  this.activeDialog = data.message;
            //}
            //else {
            //  this.id = data.id;
            //  this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            //}
            //this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }

  actionButton_previousApplicationsGrid_datagrid_view_Clicked(event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.concessionsApplication.prefix, ROUTE_CONSTANTS.concessionsApplicationDetail.prefix, event.event.data.id]));
    window.open(url, '_blank');
  }

  actionButton_personGrid_datagrid_view_Clicked(event: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/', ROUTE_CONSTANTS.person.prefixDetail, event.event.data.id]));
    window.open(url, '_blank');
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.navigateBackToHome();
  }

  dialogButton_assignApplicationUserPopup_Clicked($event: any): void {
    this.assignApplicationUser($event as ApplicationUserAssignRequestPopupQuestion);
  }

  dialogButton_addCentrelinkVerifiedAddressPopup_Clicked($event: any): void {
    const saveDataRequest: CentrelinkVerifiedAddressSaveRequest = {
      personId: this.response.personId,
      streetNumberName: $event.streetNumberName,
      suburb: $event.suburb,
      state: $event.state,
      postCode: $event.postCode
    }

    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.person.saveCentrelinkVerifiedAddress, saveDataRequest)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner()))
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            this.reloadPage(true);
          }
        })
    );
  }

  dialogButton_putApplicationOnHoldPopup_Clicked($event: any): void {
    this.putApplicationOnHold($event as ConcessionsApplicationOnHoldPopupQuestion);
  }

  dialogButton_furtherReviewRequiredPopup_Clicked($event: any): void {
    this.applicationFurtherReviewRequired($event as ConcessionsApplicationFurtherReviewRequiredPopupQuestion);
  }

  dialogButton_rejectApplicationPopup_Clicked(event: any): void {
    this.rejectApplication(event as ApplicationRejectPopupQuestion);
  }

  private saveData(request: SaveDataRequest) {

  }

  private home(): void {
    this.navigateBackToHome();
  }

  private navigateBackToHome(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    this.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.list.prefix, ROUTE_CONSTANTS.concessionsApplication.list.myApplicationsSegment], ignoreUnsavedChanges, navExtras);
  }

  private getNavigationExtras(): NavigationExtras {
    return {
      state: {
        applicationFilterRequest: history.state.applicationFilterRequest
      }
    };
  }
}
